/* Les briques de la charte elle-même.
 *
 * Volontairement maigres : tout ce que la charte expose doit venir des classes
 * de l'application. Ce qui est ici ne sert qu'à poser le cadre autour — un
 * titre de bloc, un échantillon, une rangée d'états — sinon la page montrerait
 * sa propre mise en forme plutôt que le système.
 */

import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Tag } from "@/ui/primitives";
import { SEUIL_GRAND, SEUIL_TEXTE, contrasteDe, verdict } from "@/domain/contraste";

/* ───────────────────────────── lecture des jetons ──────────────────────────
   On interroge la feuille de style plutôt que de recopier les valeurs : ce qui
   s'affiche ici EST ce que l'application applique. Un document se recopie, et
   une copie diverge ; une lecture, non. */

export function useJetons(): (nom: string) => string {
  const [, refaire] = useState(0);

  useEffect(() => {
    const obs = new MutationObserver(() => refaire((n) => n + 1));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const suivre = () => refaire((n) => n + 1);
    media.addEventListener("change", suivre);
    return () => {
      obs.disconnect();
      media.removeEventListener("change", suivre);
    };
  }, []);

  return (nom: string) =>
    getComputedStyle(document.documentElement).getPropertyValue(nom).trim();
}

/**
 * À quoi sert une marche, vu son contraste.
 *
 * Une marche claire n'« échoue » pas : elle n'est simplement pas faite pour
 * porter du texte. Marquer en rouge tout ce qui passe sous 4,5 dans une rampe
 * apprend à ignorer le rouge — on qualifie donc l'usage plutôt que de
 * sanctionner la valeur.
 */
export function Usage({
  avant, arriere, lire,
}: { avant: string; arriere: string; lire: (n: string) => string }) {
  const r = contrasteDe(lire(avant), lire(arriere));
  if (r == null) return null;
  const chiffre = r.toFixed(2).replace(".", ",");
  if (r >= SEUIL_TEXTE) return <Tag ton="ok">{chiffre} · texte</Tag>;
  if (r >= SEUIL_GRAND) return <Tag ton="warn">{chiffre} · grand texte</Tag>;
  return <Tag>{chiffre} · fond</Tag>;
}

/** Le verdict de contraste d'une paire, en étiquette. */
export function Contraste({
  avant, arriere, seuil = SEUIL_TEXTE, lire,
}: { avant: string; arriere: string; seuil?: number; lire: (n: string) => string }) {
  const r = contrasteDe(lire(avant), lire(arriere));
  const v = verdict(r, seuil);
  if (r == null) return null;
  return (
    <Tag ton={v === "non" ? "crit" : v === "juste" ? "warn" : "ok"}>
      {r.toFixed(2).replace(".", ",")}
    </Tag>
  );
}

export function BlocT({ children, note }: { children: ReactNode; note?: string }) {
  return (
    <h3 className="bloc-t">
      {children}
      {note ? <span className="bloc-t-n">{note}</span> : null}
    </h3>
  );
}

export function BlocP({ children }: { children: ReactNode }) {
  return <p className="bloc-p">{children}</p>;
}

export function Regle({ titre, children }: { titre: string; children: ReactNode }) {
  return (
    <div className="regle">
      <h3 className="regle-t">{titre}</h3>
      <p className="regle-p">{children}</p>
    </div>
  );
}

/** Un composant documenté : à quoi il sert, puis ce qu'il rend. */
export function Demo({
  quoi, note, children,
}: { quoi: string; note?: string; children: ReactNode }) {
  return (
    <div className="demo">
      <div className="demo-h">
        <code>{quoi}</code>
        {note ? <span>{note}</span> : null}
      </div>
      <div className="demo-b">{children}</div>
    </div>
  );
}

/** La rangée des états : ce qu'un composant fait sous la main. */
export function Etats({ children }: { children: ReactNode }) {
  return <div className="etats">{children}</div>;
}

export function Etat({ nom, children }: { nom: string; children: ReactNode }) {
  return (
    <div className="etat">
      <div className="etat-n">{nom}</div>
      <div className="etat-b">{children}</div>
    </div>
  );
}

/**
 * Le tableau des propriétés d'un composant.
 *
 * Sous 560 px il se replie en blocs : quatre colonnes de code sur un
 * téléphone, ça tient sans déborder mais ça ne se lit pas. La charte dit
 * qu'une table trop large devient des blocs — elle se l'applique à elle-même.
 */
export function Api({
  lignes,
}: { lignes: { nom: string; type: string; defaut?: string; quoi: string }[] }) {
  return (
    <div className="tablewrap">
      <table className="api">
        <thead>
          <tr>
            <th>Propriété</th>
            <th>Type</th>
            <th>Défaut</th>
            <th>Rôle</th>
          </tr>
        </thead>
        <tbody>
          {lignes.map((l) => (
            <tr key={l.nom}>
              <td data-l="Propriété"><code>{l.nom}</code></td>
              <td data-l="Type"><code className="api-t">{l.type}</code></td>
              <td data-l="Défaut">{l.defaut ? <code className="api-t">{l.defaut}</code> : "—"}</td>
              <td data-l="Rôle">{l.quoi}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Une note d'accessibilité : ce que le composant garantit déjà. */
export function A11y({ children }: { children: ReactNode }) {
  return (
    <p className="a11y">
      <span>Accessibilité</span>
      {children}
    </p>
  );
}

/** À faire / à ne pas faire, côte à côte. Le contre-exemple enseigne plus. */
export function Juste({
  oui, non,
}: { oui: { quoi: string; vue: ReactNode }; non: { quoi: string; vue: ReactNode } }) {
  return (
    <div className="juste">
      <div className="juste-c oui">
        <div className="juste-t">À faire · {oui.quoi}</div>
        <div className="juste-v">{oui.vue}</div>
      </div>
      <div className="juste-c non">
        <div className="juste-t">À éviter · {non.quoi}</div>
        <div className="juste-v">{non.vue}</div>
      </div>
    </div>
  );
}
